/**
 * HowMuchToEatSection - Wrapper component for the "How much to eat" section in MealDetails
 *
 * This component:
 * 1. Wraps the legacy HowMuchToEat component with new context integration
 * 2. Handles the section styling and click behavior to open editMealBatches
 * 3. Calculates proper scaling factors for leftovers
 * 4. Manages multi-dish display with proper dish numbering
 */

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import HowMuchToEat from '@components/Recipes/HowMuchToEat.react';
import { useMealPlanData, useModalControls } from '@components/Plans/Editor/contexts';
import { getPrimaryMeal, getParticipantsForSpecificMeal } from '@utils/Meals';
import { calculateTotalServingsForParticipants, getBaseServings } from '@utils/servingsCalculations';

const HowMuchToEatSection = ({ 
    meals, 
    participants, 
    profile,
    recipes,
    sectionClass,
    onChangePortionAmount
}) => {
    const { plan } = useMealPlanData();
    const { editMealBatches } = useModalControls();

    // Calculate parent meal and leftover relationship
    const primaryMeal = meals[0];
    const parent = useMemo(() => {
        if (primaryMeal?.meal_type === 'leftover' && primaryMeal?.parent) {
            return plan?.items?.find(meal => 
                meal.id === primaryMeal.parent && !meal.deleted
            );
        }
        return null;
    }, [primaryMeal, plan]);

    const hasChild = useMemo(() => {
        if (!primaryMeal?.id) return false;
        return plan?.items?.some(meal => 
            meal.parent === primaryMeal.id && 
            meal.meal_type === 'leftover' && 
            !meal.deleted
        );
    }, [primaryMeal, plan]);

    // Calculate surplus state (last day of leftovers)
    const surplus = useMemo(() => {
        if (!parent) {
            // For non-leftover meals, surplus is true if there are no child leftovers
            return !hasChild;
        }

        const daysPassed = primaryMeal.offset - parent.offset;
        const totalDays = plan?.items?.filter(meal =>
            meal.parent === parent.id &&
            meal.meal_type === 'leftover' &&
            !meal.deleted
        ).length + 1; // +1 for original meal

        return totalDays - daysPassed === 1;
    }, [parent, primaryMeal, plan, hasChild]);

    // Calculate total servings allocated to leftover meals
    const leftoverServingsPerDish = useMemo(() => {
        if (!primaryMeal?.id || !plan?.items || parent) {
            // Don't calculate for leftover meals (when parent exists)
            return meals.map(() => 0);
        }

        // Find all child leftover meals
        const childLeftovers = plan.items.filter(meal =>
            meal.parent === primaryMeal.id &&
            meal.meal_type === 'leftover' &&
            !meal.deleted
        );

        if (childLeftovers.length === 0) {
            return meals.map(() => 0);
        }

        // Calculate per-dish leftover servings
        return meals.map((meal, dishIndex) => {
            const dish = getPrimaryMeal([meal], recipes);
            if (!(dish && dish.recipe)) {
                return 0;
            }

            let totalLeftoverServings = 0;

            // For each leftover meal, calculate servings for this specific dish
            childLeftovers.forEach(leftoverMeal => {
                // Get participants for this leftover meal
                const leftoverParticipants = getParticipantsForSpecificMeal(leftoverMeal, profile);

                // Calculate servings for this leftover meal's participants on this dish
                const dishContent = dish.recipe;
                totalLeftoverServings += calculateTotalServingsForParticipants(
                    leftoverParticipants,
                    leftoverMeal,
                    dishContent
                );
            });

            return totalLeftoverServings;
        });
    }, [primaryMeal, plan, parent, meals, recipes, profile]);

    // Create tabs for each dish
    const eatThisMuchTabs = useMemo(() => {
        return meals
            .map((meal, i) => {
                const dish = getPrimaryMeal([meal], recipes);

                if (!(dish && dish.recipe)) {
                    return null;
                }

                let factor = 1;
                let scaling = meal.scaling;

                // Handle leftover calculations
                if (parent) {
                    scaling = parent.scaling;
                    const daysPassed = meal.offset - parent.offset;

                    if (daysPassed >= 1) {
                        const recipe = dish.recipe;
                        const baseServings = getBaseServings(recipe);
                        const totalYield = scaling * baseServings;

                        // Calculate actual servings consumed on previous days
                        // We need to look at all leftover meals between parent and current meal
                        let servingsConsumedOnPreviousDays = 0;

                        // Get all leftover meals from this parent, sorted by offset
                        const allLeftovers = plan?.items?.filter(m =>
                            m.parent === parent.id &&
                            m.meal_type === 'leftover' &&
                            m.offset < meal.offset &&
                            !m.deleted
                        ) || [];

                        // Add servings consumed on original parent meal
                        const parentParticipants = getParticipantsForSpecificMeal(parent, profile);
                        servingsConsumedOnPreviousDays += calculateTotalServingsForParticipants(
                            parentParticipants,
                            parent,
                            recipe
                        );

                        // Add servings consumed on each previous leftover meal
                        allLeftovers.forEach(leftoverMeal => {
                            const leftoverParticipants = getParticipantsForSpecificMeal(leftoverMeal, profile);
                            servingsConsumedOnPreviousDays += calculateTotalServingsForParticipants(
                                leftoverParticipants,
                                leftoverMeal,
                                recipe
                            );
                        });

                        const remainingServings = totalYield - servingsConsumedOnPreviousDays;
                        factor = remainingServings > 0 ? remainingServings / totalYield : 0;
                    }
                }

                return (
                    <section key={i} data-title={`Dish ${i + 1}`}>
                        <HowMuchToEat
                            dishNumber={meals.length > 1 ? i + 1 : 0}
                            uuid={meal.uuid || meal.id}
                            meal={meal}
                            profile={profile}
                            recipe={dish.recipe}
                            participants={participants}
                            factor={factor}
                            scaling={scaling}
                            surplus={surplus}
                            leftoverServings={leftoverServingsPerDish[i] || 0}
                            onClick={() => editMealBatches && editMealBatches(meal)}
                            isLeftover={!!parent}
                            onChangePortionAmount={onChangePortionAmount}
                        />
                    </section>
                );
            })
            .filter(Boolean);
    }, [meals, recipes, parent, participants, profile, surplus, leftoverServingsPerDish, editMealBatches, onChangePortionAmount]);

    const handleSectionClick = useCallback(() => {
        if (primaryMeal && editMealBatches) {
            editMealBatches(primaryMeal);
        }
    }, [editMealBatches, primaryMeal]);

    if (eatThisMuchTabs.length === 0) {
        return null;
    }

    return (
        <div 
            className={classnames('details-section', sectionClass)}
            onClick={handleSectionClick}
            style={{ cursor: 'pointer' }}
        >
            <header>
                <div className="section-title">
                    {primaryMeal?.meal_type === 'leftover' && parent ? (
                        <div className="leftover-badge">
                            Leftover from Day {parseInt(parent.offset) + 1}
                        </div>
                    ) : null}
                    <p className="t2">
                        How much to eat
                    </p>
                </div>
            </header>
            
            <div className="section-body">
                {eatThisMuchTabs}
            </div>
        </div>
    );
};

HowMuchToEatSection.propTypes = {
    meals: PropTypes.array.isRequired,
    participants: PropTypes.array.isRequired,
    profile: PropTypes.object.isRequired,
    recipes: PropTypes.object.isRequired,
    sectionClass: PropTypes.string,
    onChangePortionAmount: PropTypes.func,
};

export default HowMuchToEatSection;